/**
 * Shared visual atoms for the notes feed — initials avatar, relative time,
 * attachment chip. Kept apart from NotesThread so the composer, a root note and
 * a reply all render the same person the same way.
 */
import { useState } from "react";
import { Download, Eye, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { openPendingDocumentTab } from "@/lib/open-document-tab";
import {
  formatBytes,
  noteAttachmentHref,
  type NotePerson,
} from "@/hooks/v2/useEntityNotes";

/** "Sarah Klein" → "SK"; falls back to the email, then to a neutral glyph. */
export function personInitials(person: NotePerson | null): string {
  const source = person?.name?.trim() || person?.email?.trim() || "";
  if (!source) return "··";
  const words = source.split(/[\s._@-]+/).filter(Boolean);
  const letters = words.slice(0, 2).map((w) => w[0]);
  return (letters.join("") || source[0]).toUpperCase();
}

export function personLabel(person: NotePerson | null): string {
  return person?.name?.trim() || person?.email?.trim() || "Unknown admin";
}

/**
 * Deterministic tint per person, so the same admin keeps the same avatar colour
 * across every feed — the fastest way to scan who said what.
 */
const AVATAR_TINTS = [
  "bg-petrol text-white",
  "bg-turkis-strong text-white",
  "bg-[#5B7C99] text-white",
  "bg-[#8A6FA8] text-white",
  "bg-[#C2703D] text-white",
  "bg-[#3F7D6B] text-white",
] as const;

function tintFor(person: NotePerson | null): string {
  const seed = person?.id ?? person?.email ?? "";
  if (!seed) return "bg-muted text-muted-foreground";
  let hash = 0;
  for (let i = 0; i < seed.length; i++)
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_TINTS[Math.abs(hash) % AVATAR_TINTS.length];
}

export function NoteAvatar({
  person,
  size = "md",
  className,
}: {
  person: NotePerson | null;
  size?: "sm" | "md";
  className?: string;
}) {
  return (
    <div
      className={cn(
        "shrink-0 rounded-full grid place-items-center font-bold select-none",
        size === "sm" ? "h-7 w-7 text-[10px]" : "h-9 w-9 text-xs",
        tintFor(person),
        className
      )}
      title={personLabel(person)}
      aria-hidden
    >
      {personInitials(person)}
    </div>
  );
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

/**
 * "2 hours ago" reads faster than a timestamp when triaging, but the exact
 * moment still matters in a dispute — so it lives in the tooltip.
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diff = Math.max(0, now - then);
  if (diff < MINUTE) return "just now";
  if (diff < HOUR) {
    const n = Math.floor(diff / MINUTE);
    return `${n} minute${n === 1 ? "" : "s"} ago`;
  }
  if (diff < DAY) {
    const n = Math.floor(diff / HOUR);
    return `${n} hour${n === 1 ? "" : "s"} ago`;
  }
  if (diff < WEEK) {
    const n = Math.floor(diff / DAY);
    return `${n} day${n === 1 ? "" : "s"} ago`;
  }
  if (diff < 5 * WEEK) {
    const n = Math.floor(diff / WEEK);
    return `${n} week${n === 1 ? "" : "s"} ago`;
  }
  return absoluteTime(iso, { withTime: false });
}

export function absoluteTime(
  iso: string,
  opts: { withTime?: boolean } = {}
): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(opts.withTime === false ? {} : { hour: "2-digit", minute: "2-digit" }),
  });
}

/**
 * The bucket is private, so there is no href to render up front — the link is
 * minted on click and lives for five minutes.
 *
 * Viewing and downloading are two different intents and get two different
 * buttons, both always visible. The chip used to be one button that opened the
 * file, marked with a Download icon that only appeared on hover: it did not say
 * what a click would do, and it hid the one affordance that did. Reading a
 * signed contract in the browser and saving it to disk now each have a control,
 * and the difference is real — a download link asks storage for
 * Content-Disposition (see noteAttachmentHref's `download`), which is the only
 * thing that works cross-origin.
 */
export function NoteAttachment({
  note,
}: {
  note: {
    file_url: string | null;
    file_name: string | null;
    file_size: number | null;
  };
}) {
  // Which action is minting a link, so only the button that was pressed spins.
  const [busy, setBusy] = useState<"view" | "download" | null>(null);
  if (!note.file_url) return null;

  async function open(mode: "view" | "download") {
    if (busy) return;
    setBusy(mode);
    // The tab opens inside the click: a window.open after the signing await
    // is a popup Safari blocks, silently.
    const tab = openPendingDocumentTab();
    try {
      const href = await noteAttachmentHref(note, {
        download: mode === "download",
      });
      if (!href) throw new Error("This attachment is no longer available.");
      tab.navigate(href);
    } catch (e) {
      tab.cancel();
      toast.error(
        e instanceof Error
          ? e.message
          : mode === "download"
            ? "Could not download the attachment."
            : "Could not open the attachment."
      );
    } finally {
      setBusy(null);
    }
  }

  const label = note.file_name ?? "Attachment";

  return (
    <div className="group/att mt-2 flex max-w-full items-center gap-2 rounded-xl border border-border-subtle bg-white py-2 pl-3 pr-2 transition-colors hover:border-turkis-strong">
      {/* The name is still clickable, and still means "view" — the explicit
          buttons name the actions without taking the big target away. */}
      <button
        type="button"
        onClick={() => open("view")}
        disabled={!!busy}
        title={`View ${label}`}
        className="flex min-w-0 flex-1 items-center gap-2 text-left disabled:opacity-60"
      >
        {busy === "view" ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
        ) : (
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground group-hover/att:text-petrol" />
        )}
        <span className="truncate text-[13px] font-medium text-body">
          {label}
        </span>
        {note.file_size != null && (
          <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
            {formatBytes(note.file_size)}
          </span>
        )}
      </button>
      <span className="flex shrink-0 items-center gap-0.5">
        <button
          type="button"
          onClick={() => open("view")}
          disabled={!!busy}
          aria-label={`View ${label}`}
          title="View"
          className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-turkis-fill-soft hover:text-petrol disabled:opacity-60"
        >
          <Eye className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => open("download")}
          disabled={!!busy}
          aria-label={`Download ${label}`}
          title="Download"
          className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-turkis-fill-soft hover:text-petrol disabled:opacity-60"
        >
          {busy === "download" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
        </button>
      </span>
    </div>
  );
}
